<template>
  <div>
    <toolbar :title="pageTitle" :extended="isMobile">
      <v-layout :slot="isMobile ? 'extension' : null">
        <v-toolbar-items >
          <v-btn small fab
                 class="mr-4"
                 color="accent" to="/post-load">
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn flat small right
                 class="toolbar-btn"
                 :to="{name: 'company.loads.recurring'}">
            <v-layout column class="align-center">
              <v-icon>sync</v-icon>
            </v-layout>
          </v-btn>
          <v-btn flat small right
                 class="toolbar-btn"
                 :to="{name: 'company.loads.repeat'}">
            <v-layout column class="align-center">
              <v-icon>redo</v-icon>
            </v-layout>
          </v-btn>
          <v-btn flat small right
                 class="toolbar-btn"
                 :to="{name: 'company.loads.dispatch_offers'}">
            <v-layout column class="align-center">
              <v-icon>share</v-icon>
              <v-progress-linear color="white"
                                 class="mt-2 mb-0"
                                 v-if="loadingDispatchRequests"
                                 :indeterminate="true">
              </v-progress-linear>
              <template v-else>
                {{ dispatchRequestTrucks }}
              </template>
            </v-layout>
          </v-btn>
          <v-btn flat small right
                 v-if="showSpaceTimeContinuum"
                 class="toolbar-btn "
                 :to="{name: 'company.loads.direct_dispatch'}">
            <v-layout column class="align-center">
              <v-icon>headset_mic</v-icon>
              <v-progress-linear color="white"
                                 class="mt-2 mb-0"
                                 v-if="loadingDispatchedDriverLoads"
                                 :indeterminate="true">
              </v-progress-linear>
              <template v-else>
                <span :class="{'yellow--text': dispatchedDriverPlanHoles > 0}">
                  {{dispatchedDriverPlanHoles}}
                </span>
              </template>
            </v-layout>
          </v-btn>
          <v-btn flat small right
                 v-if="hasPlannerPermission"
                 class="toolbar-btn "
                 :to="{name: 'company.loads.planner'}">
            <v-layout column class="align-center">
              <v-icon>chrome_reader_mode</v-icon>
              <v-progress-linear color="white"
                                 class="mt-2 mb-0"
                                 v-if="loadingPlannedLoads"
                                 :indeterminate="true">
              </v-progress-linear>
              <template v-else>
                {{ plannedLoadGroupTrucks }}
              </template>
            </v-layout>
          </v-btn>
          <v-btn flat small right
                 class="toolbar-btn"
                 :to="{name: 'company.loads.scheduled'}">
            <v-layout column class="align-center">
              <v-icon>schedule</v-icon>
              <v-progress-linear color="white"
                                 class="mt-2 mb-0"
                                 v-if="loadingLoads"
                                 :indeterminate="true">
              </v-progress-linear>
              <template v-else>
                {{ scheduledLoads.length }}
              </template>
            </v-layout>
          </v-btn>

          <v-btn flat small right
                 class="toolbar-btn"
                 :to="{name: 'company.loads.in_transit'}">
            <v-layout column class="align-center">
              <v-icon>local_shipping</v-icon>
              <v-progress-linear color="white"
                                 class="mt-2 mb-0"
                                 v-if="loadingLoads"
                                 :indeterminate="true">
              </v-progress-linear>
              <template v-else>
                {{ inTransitLoads.length }}
              </template>
            </v-layout>
          </v-btn>

          <v-btn flat small right
                 class="toolbar-btn "
                 :to="{name: 'company.loads.delivered'}">
            <v-layout column class="align-center">
              <v-icon>done</v-icon>
              <v-progress-linear color="white"
                                 class="mt-2 mb-0"
                                 v-if="loadingLoads"
                                 :indeterminate="true">
              </v-progress-linear>
              <template v-else>
                {{ deliveredLoads.length }}
              </template>
            </v-layout>
          </v-btn>

          <!-- historical loads dashboard button: planner/scheduler/office/admin -->
          <v-btn flat small right
                 v-if="hasHistoricalLoadsPermission"
                 class="toolbar-btn"
                 :to="{name: 'company.loads.history'}">
            <v-layout column class="align-center">
              <v-icon>history</v-icon>
            </v-layout>
          </v-btn>
        </v-toolbar-items>
      </v-layout>
    </toolbar>

    <div class="pa-3" v-if="isMobile">
      <v-text-field v-model="search" append-icon="search"
                    class="mb-2 single-line hide-details"
                    label="Search loads">
      </v-text-field>

      <v-select
        :items="mobileSortOptions"
        v-model="sortValue"
        menu-props="auto"
        label="Sort by"
        hide-details
        append-icon="sort"
        single-line
        class="mb-2"
      ></v-select>
    </div>

    <v-card>
      <router-view :company="company"
                   @updateLoad="onUpdateLoad"/>
    </v-card>
  </div>
</template>

<script>

import Toolbar from '@/components/Toolbar';
import * as Checks from '@/store/permissions';

import filterLoads from '@/mixins/filterLoads';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import companySectionMixin from '@/mixins/companySectionMixin';
import directDispatchingMixin from '@/mixins/directDispatchingMixin';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import dispatchRequestsMixin from '@/mixins/dispatchRequestsMixin';
import loadsRoutingMixin from '@/mixins/loadsRoutingMixin';

import { mapGetters } from 'vuex';

export default {
  name: 'LoadsPage',
  mixins: [
    directDispatchingMixin,
    filterLoads,
    bookedLoadMixin,
    companySectionMixin,
    dispatchRequestsMixin,
    loadsRoutingMixin,
    planningLoadGroupsMixin,
  ],
  components: {
    Toolbar,
  },
  data: () => ({
    search: null,
    loadingBtn: false,
    mobileSortOptions: [
      { text: 'Sort ascending by Pickup Date', value: 'pickup_asc' },
      { text: 'Sort descending by Pickup Date', value: 'pickup_desc' },
      { text: 'Sort ascending by driver', value: 'driver_asc' },
      { text: 'Sort descending by driver', value: 'driver_desc' },
    ],
  }),
  computed: {
    ...mapGetters('companies', {
      company: 'currentCompany',
    }),
    ...mapGetters('bookedLoads', {
      bookedLoads: 'bookedLoads',
      storeBookedLoadsCompanyId: 'companyId',
      loadingLoads: 'loadingLoads',
      bookedLoadsTime: 'timestamp',
    }),
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    ...mapGetters('plannedLoads', {
      loadingPlannedLoads: 'loadingLoads',
    }),
    pageTitle() {
      return `${this.company.companyName} : ${this.$route.meta.title || ''}`;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    hasPlannerPermission() {
      const myAccount = this.findCompanyAccount()(this.company.id);
      return this.isLtnAdmin || Checks.accountHasCreateListingPermission(myAccount);
    },
    showSpaceTimeContinuum() {
      return false;
    },
    hasHistoricalLoadsPermission() {
      const myAccount = this.findCompanyAccount()(this.company.id);
      return this.isLtnAdmin || Checks.canAccountViewCompanyBookedLoadsHistory(myAccount);
    },
    plannedLoadGroupTrucks() {
      return this.planningLoadGroupUnassignedTruckCount;
    },
    dispatchRequestTrucks() {
      return this.dispatchRequestTruckCount;
    },

    loadingDispatchedDriverLoads() {
      return this.loadingDirectDispatchAssignments;
    }  ,
    dispatchedDriverPlanHoles() {
      return this.directDispatchDriversAwaitingAssignment.length;
    },
  },
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    isActiveSection(sectionName) {
      return this.section === sectionName;
    },
    loadData() { // Called it from companySectionMixin
      this.getBookedLoads();
      this.reloadCompanyPlanningLoadGroups(this.companyId);
    },
    getBookedLoads() {
      this.$store.dispatch('bookedLoads/getBookedLoads', this.companyId);
    },
    onUpdateLoad(loadData) {
      this.$store.dispatch('bookedLoads/updateLoadTacticalInfo', loadData);
    },
  },
  created() {
    window.__insp.push(['tagSession', 'open_booked_loads']);
    this.$ga.event({
      eventCategory: 'open_section',
      eventAction: 'booked_loads',
    });

    if (this.storeBookedLoadsCompanyId !== this.companyId) {
      this.getBookedLoads();
      this.$store.dispatch('plannedLoads/getPlannedLoads', this.companyId);
      this.loadDispatchedRequests(this.companyId);
    }
  },
};
</script>

<style>
  .toolbar-btn {
    min-width: 48px;
  }

  .toolbar-btn:not(.v-btn--active) {
    opacity: .7;
  }

  .toolbar-btn.v-btn--active {
    border-bottom: 2px solid white;
  }

  .toolbar-btn:before,
  .toolbar-btn:focus:before,
  .toolbar-btn:hover:before {
    background-color: transparent !important;
  }

</style>



// WEBPACK FOOTER //
// src/pages/Companies/Loads/LoadsPage.vue